import { useEffect, useRef } from 'react';

/**
 * Scroll position of an inner scroller (a report table's own viewport) in the
 * URL — `xKey` / `yKey` (default tx / ty), so a shared link or a "Back to the
 * report" return lands on the same rows and columns, not just the same page
 * scroll. Restores once the content is tall/wide enough (rows stream in and
 * expand after load; gives up after ~10s), then follows the user's scrolling
 * with a debounced replaceState like useScrollUrlSync.
 *
 * @param {React.RefObject<HTMLElement>} ref the scrolling element
 * @param {{ enabled?: boolean, ready?: boolean, xKey?: string, yKey?: string }} opts
 *   `ready` — the content has painted (restore waits for it)
 */
export function useElementScrollUrlSync(ref, { enabled = true, ready = true, xKey = 'tx', yKey = 'ty' } = {}) {
  const restoredRef = useRef(false);
  const targetRef = useRef(null);
  if (targetRef.current === null && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const x = Number(params.get(xKey));
    const y = Number(params.get(yKey));
    targetRef.current = {
      x: Number.isFinite(x) && x > 0 ? x : 0,
      y: Number.isFinite(y) && y > 0 ? y : 0,
    };
  }

  useEffect(() => {
    if (!enabled || restoredRef.current) return undefined;
    const { x, y } = targetRef.current || { x: 0, y: 0 };
    if (!x && !y) {
      restoredRef.current = true;
      return undefined;
    }
    if (!ready) return undefined;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const el = ref.current;
      if (!el) return;
      const fitsY = el.scrollHeight - el.clientHeight >= y;
      const fitsX = el.scrollWidth - el.clientWidth >= x;
      if ((fitsY && fitsX) || attempts >= 40) {
        clearInterval(timer);
        el.scrollTop = y;
        el.scrollLeft = x;
        restoredRef.current = true;
      }
    }, 250);
    return () => clearInterval(timer);
  }, [enabled, ready, ref]);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    let timer = null;
    const onScroll = () => {
      if (!restoredRef.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        const x = Math.round(el.scrollLeft);
        const y = Math.round(el.scrollTop);
        if (x > 0) params.set(xKey, String(x));
        else params.delete(xKey);
        if (y > 0) params.set(yKey, String(y));
        else params.delete(yKey);
        const qs = params.toString();
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
        );
      }, 300);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (timer) clearTimeout(timer);
      el.removeEventListener('scroll', onScroll);
    };
  }, [enabled, ref, xKey, yKey]);
}

export default useElementScrollUrlSync;
